import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { type InputProps, SupInput } from "../../../components/UI/Input/Input";
import { useControlledState } from "../../../hooks/useControlledState";
import SupCaretUp from "../../../icons/CaretUp";
import SupCaretDown from "../../../icons/CaretDown";
import { countDecimals } from "../../../utils/numbers";

export interface NumberInputProps extends Omit<InputProps, "formatter" | "type" | "clearable"> {
  step?: number;
  min?: number;
  max?: number;
}

const NUMBER_REG_EXP = /^-?\d*\.?\d*$/;

function numberFormatter(value: string, prevValue?: string) {
  if (NUMBER_REG_EXP.test(value)) {
    return value;
  }

  return prevValue ?? "";
}

export const SupNumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function SupNumberInput(
  { value, defaultValue, min = -Infinity, max = Infinity, step = 1, onChange, suffixIcon, ...inputProps },
  ref
) {
  const [inputValue = "", setInputValue] = useControlledState<string | undefined>({
    value,
    defaultValue,
    onStateChange: onChange,
  });

  const incrementValue = () => {
    const numericValue = parseFloat(inputValue);

    if (!isNaN(numericValue)) {
      const numberOfDecimal = Math.max(countDecimals(numericValue), countDecimals(step));
      const newValue = numericValue + step;

      setInputValue(newValue <= max ? newValue.toFixed(numberOfDecimal) : inputValue);
      return;
    }

    setInputValue(min !== -Infinity ? min.toString() : "0");
  };

  const decrementValue = () => {
    const numericValue = parseFloat(inputValue);

    if (!isNaN(numericValue)) {
      const numberOfDecimal = Math.max(countDecimals(numericValue), countDecimals(step));
      const newValue = numericValue - step;

      setInputValue(newValue >= min ? newValue.toFixed(numberOfDecimal) : inputValue);
      return;
    }

    setInputValue(max !== Infinity ? max.toString() : "0");
  };

  function shouldDisableControlBtn(type: "increment" | "decrement") {
    const numericValue = parseFloat(inputValue);

    if (isNaN(numericValue)) {
      return false;
    }

    if (type === "increment") {
      return numericValue + step > max;
    }

    return numericValue - step < min;
  }

  const controls = (
    <div className="flex items-center gap-2">
      {suffixIcon}
      <span
        className="inline-flex flex-col w-5 h-10"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <button onClick={incrementValue} disabled={shouldDisableControlBtn("increment")} className="disabled:opacity-44">
          <SupCaretUp />
        </button>
        <button onClick={decrementValue} disabled={shouldDisableControlBtn("decrement")} className="disabled:opacity-44">
          <SupCaretDown />
        </button>
      </span>
    </div>
  );

  return (
    <SupInput
      ref={ref}
      value={inputValue}
      formatter={numberFormatter}
      onChange={setInputValue}
      {...inputProps}
      suffixIcon={controls}
      inputProps={{ inputMode: "numeric", pattern: "d*", className: twMerge(suffixIcon ? "pr-16" : "", inputProps.inputProps?.className), ...inputProps.inputProps }}
    />
  );
});
